namespace $ {

	export class $shm_hitalama_map_journal_analysis_widgets {

		static ensure_if_ready(
			map: $shm_hitalama_map,
			journal: $shm_hitalama_map_journal_snapshots | null | undefined,
		) {
			if( !journal ) return { ready: false, reason: 'no_journal' }
			if( !journal.write_stopped() ) return { ready: false, reason: 'not_stopped' }
			const client_list = journal.clients()
			const clients = client_list.length
			const run = journal.run_id()
			const owner = client_list[0] ?? ''
			if( owner && journal.client_id() !== owner ) {
				return {
					ready: false,
					reason: 'not_analysis_owner',
					run,
					clients,
					written: journal.snapshots_written_count(),
					owner,
					client: journal.client_id(),
				}
			}
			if( run && clients > 0 && journal.snapshots_written_count() >= clients ) {
				this.ensure( map, journal, run )
				return {
					ready: true,
					reason: 'created',
					run,
					clients,
					written: journal.snapshots_written_count(),
					store: map.store( 'benchmark_analysis_run' ) ?? null,
				}
			}
			return {
				ready: false,
				reason: 'not_ready',
				run,
				clients,
				written: journal.snapshots_written_count(),
			}
		}

		static widget_by_title( map: $shm_hitalama_map, title: string ) {
			return map.Widgets_list()?.remote_list().find( widget => widget.title() === title ) ?? null
		}

		@ $mol_action
		static widget_make(
			map: $shm_hitalama_map,
			type: $shm_hitalama_map_widget_def_type_name,
			pos: readonly [number, number],
			width: number,
			height: number,
			title: string,
			text = '',
		) {
			const existing = this.widget_by_title( map, title )
			if( existing ) return existing

			const area = map.land().area_make()
			const widget = $giper_baza_glob.Pawn( area.link(), $shm_hitalama_map_widget )

			const time = ( new $mol_time_moment ).valueOf()
			widget.Created_at( null )?.val( time )
			map.Changed_at( null )?.val( time )
			map.Widget_by_name( null )?.key( title, 'auto' ).remote( widget )
			widget.text_crdt_disabled( true )
			widget.Map( null )?.remote( map )
			widget.render_data_patch({
				title,
				type,
				text,
				body_x: pos[0],
				body_y: pos[1],
				right_edge_x: width,
				bottom_edge_y: height,
				opacity: 1,
			})

			return widget
		}

		@ $mol_action
		static widget_publish( map: $shm_hitalama_map, widget: $shm_hitalama_map_widget | null | undefined ) {
			if( !widget ) return null
			const list = map.Widgets_list( null )
			if( !list ) return null
			if( !list.remote_list().some( item => item.link_str() === widget.link_str() ) ) {
				list.add( widget.link() )
			}
			return widget
		}

		@ $mol_action
		static widget_add(
			map: $shm_hitalama_map,
			type: $shm_hitalama_map_widget_def_type_name,
			pos: readonly [number, number],
			width: number,
			height: number,
			title: string,
			text = '',
		) {
			const widget = this.widget_make( map, type, pos, width, height, title, text )
			return this.widget_publish( map, widget )
		}

		@ $mol_action
		static ensure(
			map: $shm_hitalama_map,
			journal: $shm_hitalama_map_journal_snapshots,
			run: string,
		) {
			const key = 'benchmark_analysis_run'
			const signature_key = 'benchmark_analysis_signature'
			const analysis = $shm_hitalama_map_journal_analysis.data( journal, run )
			if( !analysis ) return

			const summary_name = `Benchmark analysis ${ run }`
			const details_name = `Benchmark analysis events ${ run }`
			const chart_table_name = `Benchmark analysis chart data ${ run }`
			const chart_name = `Benchmark analysis chart ${ run }`

			const existing_chart_table = this.widget_by_title( map, chart_table_name )?.Table()?.remote()
			const existing_chart = this.widget_by_title( map, chart_name )?.Chart()?.remote()
			if(
				map.store( key ) === run
				&& map.store( signature_key ) === analysis.signature
				&& existing_chart_table?.table_head()?.[0] === 'event'
				&& existing_chart_table?.table_head()?.length >= analysis.client_labels.length + 1
				&& existing_chart_table?.table_rows()?.length >= analysis.chart_rows.length
				&& existing_chart?.axis() === 'event'
				&& existing_chart?.values()?.length >= analysis.client_labels.length
			) return

			const summary = this.widget_by_title( map, summary_name )
				?? this.widget_add( map, 'text_md', [ 120, 80 ], 460, 260, summary_name, analysis.summary_text )
			if( !summary ) return
			if( summary.type() !== 'text_md' ) return
			summary.text( analysis.summary_text )
			summary.auto_height( true )

			const details = this.widget_by_title( map, details_name )
				?? this.widget_add( map, 'text_md', [ 600, 80 ], 760, 320, details_name, analysis.details_text )
			if( !details ) return
			if( details.type() !== 'text_md' ) return
			details.text( analysis.details_text )
			details.auto_height( true )

			const chart_table_existing = this.widget_by_title( map, chart_table_name )
			const chart_table = chart_table_existing
				?? this.widget_make( map, 'table', [ 120, 380 ], 520, 260, chart_table_name )
			if( !chart_table ) return
			if( chart_table.type() !== 'table' ) return
			const chart_table_entity = chart_table.Table()?.remote() ?? map.land()
				.Pawn( $shm_hitalama_map_widget_entity_table )
				.Head( map.land().self_make( $mol_hash_string( chart_table_name ) ) )
			if( !chart_table_entity ) return
			if( !map.Tables( null )?.remote_list().some( item => item.link().str === chart_table_entity.link().str ) ) {
				map.Tables( null )?.remote_add( chart_table_entity )
			}
			chart_table.Table( null )?.remote( chart_table_entity )
			chart_table_entity.Map( null )?.remote( map )
			const chart_rows_prev = chart_table_entity.table_rows()
			const chart_rows_next = chart_rows_prev.length > analysis.chart_rows.length ? chart_rows_prev : analysis.chart_rows
			chart_table_entity.table_head([ 'event', ... analysis.client_labels ])
			chart_table_entity.table_rows( chart_rows_next )
			this.widget_publish( map, chart_table )

			const chart_widget_existing = this.widget_by_title( map, chart_name )
			const chart_widget = chart_widget_existing
				?? this.widget_make( map, 'echarts', [ 680, 430 ], 680, 420, chart_name )
			if( !chart_widget ) return
			if( chart_widget.type() !== 'echarts' ) return
			chart_widget.Table( null )?.remote( chart_table_entity )
			const chart = chart_widget.Chart()?.remote() ?? map.land()
				.Pawn( $shm_hitalama_map_widget_entity_chart )
				.Head( map.land().self_make( $mol_hash_string( chart_name ) ) )
			if( !chart ) return
			if( !map.Charts( null )?.remote_list().some( item => item.link().str === chart.link().str ) ) {
				map.Charts( null )?.remote_add( chart )
			}
			chart_widget.Chart( null )?.remote( chart )
			chart.Widget( null )?.remote( chart_widget )
			chart.axis( 'event' )
			chart.values( analysis.client_labels )
			chart.groups( [] )
			chart_widget.Data( null )?.val({
				chart_type: 'line',
				legend_show: true,
				toolbox_show: true,
				hitalama_version: analysis.app_version,
			})
			this.widget_publish( map, chart_widget )

			const ready_chart_table = chart_table.Table()?.remote()
			const ready_chart = chart_widget.Chart()?.remote()
			if(
				ready_chart_table?.table_head()?.[0] !== 'event'
				|| ready_chart_table?.table_head()?.length < analysis.client_labels.length + 1
				|| ready_chart_table?.table_rows()?.length < analysis.chart_rows.length
				|| ready_chart?.axis() !== 'event'
				|| ready_chart?.values()?.length < analysis.client_labels.length
			) return

			map.store( key, run )
			map.store( signature_key, analysis.signature )
			$$.$shm_hitalama_notifications.close( `hitalama_benchmark_collect_${ map.link().str }` )
			$$.$shm_hitalama_notifications.close( `hitalama_benchmark_clients_${ map.link().str }` )
			journal.write_stopped( false )
			journal.write_started( false )
			map.Changed_at( null )?.val( ( new $mol_time_moment ).valueOf() )
		}

	}

}
